/**
 * Studio - Music Track Sync Page
 * Create and sync drum tracks with YouTube videos
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { ScoreV2, DrumlyExportV1 } from "./types";
import { buildTempoIndex, tickToMsFast, lastTickOf, maskAtMs } from "./tempoUtils";
import { parseSongsterrDrumsToScoreV2 } from "./parser";
import { buildDrumlyExportV1, downloadJson } from "./exportBuilder";
import {
  parseYoutubeVideoId,
  ensureYoutubeIframeApi,
  setPlaybackRateSafe,
} from "./youtubeUtils";
import FallingNotesStage from "./components/FallingNotesStage";
import TimelineEditor from "./components/TimelineEditor";
import { createSongV2 } from "../songV2/songV2Api";
import "./StudioPage.css";

const APP_VERSION = 10;

const PPQ_BASE = 1920;
const PPQ_MULT = 8;

export default function StudioPage() {
  const navigate = useNavigate();
  const [youtubeUrl, setYoutubeUrl] = useState(
    "https://www.youtube.com/watch?v=0J2QdDbelmY"
  );
  const { videoId } = useMemo(
    () => parseYoutubeVideoId(youtubeUrl.trim()),
    [youtubeUrl]
  );
  // YouTube her zaman 0'dan başlar - startSec kullanılmıyor
  const startSec = 0;

  // JSON parsing
  const [jsonText, setJsonText] = useState("");
  const [parseError, setParseError] = useState<string>("");
  const [parseOk, setParseOk] = useState(false);

  const [scoreLive, setScoreLive] = useState<ScoreV2 | null>(null);
  const tempoIndexLive = useMemo(
    () => (scoreLive ? buildTempoIndex(scoreLive.tempo_map ?? [], scoreLive.ppq) : []),
    [scoreLive]
  );

  const trackObjRef = useRef<any>(null);

  // Sync + position
  const [syncMs, setSyncMs] = useState(0);
  const [posMs, setPosMs] = useState(0);
  const [ytMsNow, setYtMsNow] = useState(0);

  // Export + preview mode
  const [exportJson, setExportJson] = useState<DrumlyExportV1 | null>(null);
  const exportJsonRef = useRef<DrumlyExportV1 | null>(null); // For YouTube callback closure
  const [previewMode, setPreviewMode] = useState<"live" | "export">("live");
  const [needsRestart, setNeedsRestart] = useState(false);
  
  // Timeline Editor - düzenlenen events (henüz kaydedilmemiş)
  const [editedEvents, setEditedEvents] = useState<Array<{ ms: number; m: number }> | null>(null);
  const hasUnsavedChanges = editedEvents !== null;

  // UI tuning
  const [lookaheadMs, setLookaheadMs] = useState(2200);
  const [hitMs, setHitMs] = useState(80);

  // YouTube player state
  const ytHostRef = useRef<HTMLDivElement | null>(null);
  const ytPlayerRef = useRef<any>(null);
  const [ytReady, setYtReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const rafRef = useRef<number>(0);

  // Speed
  const [speed, setSpeed] = useState(1.0);

  // Derived active score (live vs export)
  // Export mode'da dt/m array'lerinden score oluştur (Timeline Editor değişikliklerini yansıtır)
  const scoreExport: ScoreV2 | null = useMemo(() => {
    // If there are edited events from Timeline Editor, use them for live preview
    if (editedEvents) {
      const events: Array<{ t0: number; dt: number; m: number }> = [];
      let absMs = 0;
      for (let i = 0; i < editedEvents.length; i++) {
        if (i === 0) {
          absMs = editedEvents[0].ms;
        } else {
          absMs += editedEvents[i].ms;
        }
        events.push({
          t0: absMs,
          dt: editedEvents[i].ms,
          m: editedEvents[i].m || 0,
        });
      }
      return {
        ppq: 1,
        tempo_map: [{ tick: 0, bpm: 60000 }],
        events,
      };
    }
    
    if (!exportJson?.dt || !exportJson?.m) return null;
    
    // dt/m array'lerinden events oluştur
    // dt[0] = first event absolute ms, dt[i] = delta from previous
    const events: Array<{ t0: number; dt: number; m: number }> = [];
    let absMs = 0;
    for (let i = 0; i < exportJson.dt.length; i++) {
      if (i === 0) {
        absMs = exportJson.dt[0];
      } else {
        absMs += exportJson.dt[i];
      }
      events.push({
        t0: absMs, // ms cinsinden (ppq=1, bpm=60000 ile 1 tick = 1 ms)
        dt: exportJson.dt[i],
        m: exportJson.m[i] || 0,
      });
    }
    
    // ppq=1, bpm=60000 → tickToMs hesabı: (tick / 1) * (60000 / 60000) = tick
    // Yani t0 değeri direkt ms olarak kullanılır
    return {
      ppq: 1,
      tempo_map: [{ tick: 0, bpm: 60000 }],
      events,
    };
  }, [exportJson, editedEvents]);

  // Keep ref in sync with state for YouTube callback closure
  useEffect(() => {
    exportJsonRef.current = exportJson;
  }, [exportJson]);

  const activeScore = previewMode === "export" ? scoreExport : scoreLive;
  const activeTempoIndex = useMemo(
    () => (activeScore ? buildTempoIndex(activeScore.tempo_map ?? [], activeScore.ppq) : []),
    [activeScore]
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const durationMs = useMemo(() => {
    if (!activeScore) return 0;
    return tickToMsFast(lastTickOf(activeScore), activeTempoIndex, activeScore.ppq);
  }, [activeScore, activeTempoIndex]);

  const activeMask = useMemo(() => {
    if (!activeScore) return 0;
    return maskAtMs(activeScore, activeTempoIndex, posMs);
  }, [activeScore, activeTempoIndex, posMs]);

  // Auto-parse JSON (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      const txt = jsonText.trim();
      if (!txt) {
        setParseOk(false);
        setParseError("");
        setScoreLive(null);
        trackObjRef.current = null;
        return;
      }

      try {
        const obj = JSON.parse(txt);
        trackObjRef.current = obj;

        const bpm = Number(obj?.bpm || 120);
        const quarterMs = 60000 / (bpm > 0 ? bpm : 120);
        const sixteenthMs = quarterMs / 4;
        const autoFlashMs = Math.round(sixteenthMs * 0.8);

        const sc = parseSongsterrDrumsToScoreV2(obj, {
          ppqBase: PPQ_BASE,
          ppqMultiplier: PPQ_MULT,
          flashMs: autoFlashMs,
        });
        setScoreLive(sc);
        setParseOk(true);
        setParseError("");

        // syncMs=0 olarak başla, kullanıcı Timeline Editor'dan ayarlar
        if (sc.events.length) {
          const ti = buildTempoIndex(sc.tempo_map, sc.ppq);
          const firstEventMs = tickToMsFast(sc.events[0].t0, ti, sc.ppq);
          
          console.log('[Auto-Sync] First event at tick:', sc.events[0].t0, '→', firstEventMs, 'ms');
          console.log('[Auto-Sync] syncMs: 0 (kullanıcı ayarlayacak)');
          
          // Kaydedilmiş değer varsa onu kullan
          const saved = localStorage.getItem(`drumly:syncMs:${videoId}`);
          if (saved != null && saved.trim() !== "") {
            const n = Number(saved);
            if (Number.isFinite(n)) setSyncMs(Math.round(n));
          } else {
            setSyncMs(0);
          }
        }
      } catch (e: any) {
        setParseOk(false);
        setParseError(e?.message || "Invalid JSON");
        setScoreLive(null);
        trackObjRef.current = null;
      }
    }, 450);

    return () => clearTimeout(t);
  }, [jsonText, startSec, videoId]);

  // Build/refresh YouTube player when videoId changes
  useEffect(() => {
    let cancelled = false;

    (async () => {
      await ensureYoutubeIframeApi();
      if (cancelled) return;
      if (!ytHostRef.current) return;

      try {
        ytPlayerRef.current?.destroy?.();
      } catch {}
      ytPlayerRef.current = null;
      setYtReady(false);
      setIsPlaying(false);

      ytPlayerRef.current = new window.YT.Player(ytHostRef.current, {
        width: "100%",
        height: "360",
        videoId,
        playerVars: { playsinline: 1, controls: 1, rel: 0 },
        events: {
          onReady: () => {
            setYtReady(true);
            setPlaybackRateSafe(ytPlayerRef.current, speed);
          },
          onStateChange: (e: any) => {
            const st = e?.data;
            console.log('[YouTube onStateChange] State:', st, 'PLAYING:', window.YT.PlayerState.PLAYING);
            if (st === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              // Auto-export when play is pressed - use ref to get current value
              const currentExportJson = exportJsonRef.current;
              console.log('[Auto-Export] Play detected - exportJson exists:', !!currentExportJson, 'scoreLive exists:', !!scoreLive);
              if (!currentExportJson && scoreLive) {
                console.log('[Auto-Export] Building export with current syncMs:', syncMs);
                buildExport();
              }
            } else {
              setIsPlaying(false);
            }
          },
        },
      });
    })();

    return () => {
      cancelled = true;
      try {
        ytPlayerRef.current?.destroy?.();
      } catch {}
      ytPlayerRef.current = null;
      setYtReady(false);
      setIsPlaying(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  // Apply speed
  useEffect(() => {
    const p = ytPlayerRef.current;
    console.log('[Speed useEffect] Triggered - speed:', speed, 'ytReady:', ytReady, 'player exists:', !!p);
    if (!p || !ytReady) return;
    console.log('[Speed useEffect] Applying speed to YouTube player:', speed);
    setPlaybackRateSafe(p, speed);
  }, [speed, ytReady]);

  // Load saved syncMs for this video
  useEffect(() => {
    const saved = localStorage.getItem(`drumly:syncMs:${videoId}`);
    if (saved != null && saved.trim() !== "") {
      const n = Number(saved);
      if (Number.isFinite(n)) setSyncMs(Math.round(n));
    } else {
      setSyncMs(0);
    }
    setExportJson(null);
    setPreviewMode("live");
    setNeedsRestart(true);
    setPosMs(0);
    setYtMsNow(0);
  }, [videoId]);

  // RAF: posMs = ytMs - syncMs
  useEffect(() => {
    if (!isPlaying) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    const loop = () => {
      const p = ytPlayerRef.current;
      if (p && p.getCurrentTime) {
        const ytMs = Number(p.getCurrentTime() || 0) * 1000;
        setYtMsNow(ytMs);
        setPosMs(ytMs - syncMs);
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isPlaying, syncMs]);

  // Controls
  function restartToBeginningTab0() {
    const p = ytPlayerRef.current;
    if (!p || !p.seekTo) return;

    const ytMsTarget = Math.max(0, syncMs);
    p.seekTo(ytMsTarget / 1000, true);
    setPosMs(0);
    setYtMsNow(ytMsTarget);
    setNeedsRestart(false);
  }

  function togglePlay() {
    const p = ytPlayerRef.current;
    if (!p || !ytReady) return;
    if (!activeScore || !activeScore.events.length) return;

    if (isPlaying) {
      p.pauseVideo?.();
      return;
    }

    // Play'e basınca export oluştur
    if (scoreLive && !exportJson) {
      buildExport();
    }

    if (needsRestart) {
      restartToBeginningTab0();
      setTimeout(() => p.playVideo?.(), 60);
    } else {
      p.playVideo?.();
    }
  }

  // buildExport fonksiyonu
  const buildExport = useCallback((useSyncMs?: number) => {
    const p = ytPlayerRef.current;
    if (!p) return;
    if (!scoreLive) return;

    const vd = p.getVideoData?.() || {};
    const ytTitle = String(vd.title || "");
    const ytArtist = String(vd.author || "");
    const ytDurationSec = Number(p.getDuration?.() || 0);
    const exportSyncMs = useSyncMs !== undefined ? useSyncMs : syncMs;

    const out = buildDrumlyExportV1({
      score: scoreLive,
      tempoIndex: tempoIndexLive,
      syncMs: exportSyncMs,
      youtubeUrl,
      videoId,
      startSec,
      trackObj: trackObjRef.current,
      ytTitle,
      ytArtist,
      ytDurationSec,
      lookaheadMs,
      hitMs,
      calibrateSpeed: speed,
    });

    setExportJson(out);
    setPreviewMode("export");
    setNeedsRestart(true);
  }, [scoreLive, tempoIndexLive, syncMs, youtubeUrl, videoId, startSec, lookaheadMs, hitMs, speed]);

  const handleSyncMsChange = useCallback((newSyncMs: number) => {
    setSyncMs(newSyncMs);
    localStorage.setItem(`drumly:syncMs:${videoId}`, String(newSyncMs));
    if (exportJson) {
      setExportJson({ ...exportJson, sync_ms: newSyncMs });
    }
  }, [videoId, exportJson]);

  function copyExport() {
    if (!exportJson) return;
    navigator.clipboard.writeText(JSON.stringify(exportJson, null, 2));
  }

  function downloadExport() {
    if (!exportJson) return;
    const safeTitle = String(exportJson?.title || "song")
      .replace(/[^\w-]+/g, "_")
      .slice(0, 60);
    downloadJson(`${safeTitle}_drumly_v1.json`, exportJson);
  }

  async function saveToDatabase() {
    if (!exportJson) return;
    
    try {
      await createSongV2({
        ...exportJson,
        calibrate_speed: exportJson.calibrate_speed || 1.0,
      });
      alert("✅ Şarkı veritabanına kaydedildi! SongsV2 sayfasından düzenleyebilirsiniz.");
      navigate("/songsv2");
    } catch (error: any) {
      console.error("Save failed:", error);
      alert(`❌ Kaydetme hatası: ${error.response?.data?.message || error.message}`);
    }
  }

  const canPlay = Boolean(ytReady && activeScore?.events?.length);

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", padding: "var(--spacing-xl)" }}>
      <div style={{ maxWidth: 1600, margin: "0 auto" }}>
        <div style={{
          marginBottom: "var(--spacing-2xl)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--spacing-sm)",
        }}>
          <h1
            style={{
              fontSize: "var(--font-size-3xl)",
              fontWeight: "var(--font-weight-heavy)",
              color: "var(--text-primary)",
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-md)",
            }}
          >
            <span role="img" aria-label="studio">🎵</span> Studio
            <span style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)", fontWeight: "var(--font-weight-medium)" }}>v{APP_VERSION}</span>
          </h1>
          <p style={{ color: "var(--text-secondary)", fontSize: "var(--font-size-base)", margin: 0 }}>
            Create and sync drum tracks with YouTube videos
          </p>
          <div style={{ 
            fontSize: "var(--font-size-sm)", 
            color: "var(--text-muted)",
            display: "flex",
            gap: "var(--spacing-sm)",
            flexWrap: "wrap",
          }}>
            <span>📝 1. Paste JSON</span>
            <span>→</span>
            <span>▶️ 2. Play</span>
            <span>→</span>
            <span>✏️ 3. Edit</span>
            <span>→</span>
            <span>💾 4. Save</span>
          </div>
        </div>

        {/* Top Row: JSON, YouTube, Export - Responsive Grid */}
        <div
          className="studio-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "var(--spacing-lg)",
            marginBottom: "var(--spacing-xl)",
          }}
        >
          {/* LEFT: Track JSON */}
          <section
            className="glass-card"
            style={{
              background: "var(--surface-card)",
              border: "1px solid var(--surface-border)",
              borderRadius: "var(--radius-lg)",
              padding: "var(--spacing-lg)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ 
              color: "var(--text-secondary)", 
              fontWeight: "var(--font-weight-bold)", 
              marginBottom: "var(--spacing-sm)",
              fontSize: "var(--font-size-sm)",
            }}>
              📄 Track JSON
            </div>
            <textarea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              placeholder="Paste your drum track JSON data here..."
              style={{
                width: "100%",
                minHeight: 280,
                maxHeight: 400,
                borderRadius: "var(--radius-md)",
                background: "rgba(0, 0, 0, 0.3)",
                border: "1px solid var(--surface-border)",
                padding: "var(--spacing-base)",
                color: "var(--text-primary)",
                fontFamily: "var(--font-mono)",
                fontSize: "var(--font-size-xs)",
                resize: "vertical",
              }}
            />
            <div style={{ marginTop: "var(--spacing-sm)", fontSize: "var(--font-size-xs)" }}>
              {parseOk ? (
                <span style={{ color: "var(--accent-success)", fontWeight: "var(--font-weight-bold)" }}>
                  ✓ Parsed · {scoreLive?.events?.length ?? 0} events
                </span>
              ) : parseError ? (
                <span style={{ color: "var(--accent-danger)", fontWeight: "var(--font-weight-bold)" }}>
                  ✕ {parseError}
                </span>
              ) : (
                <span style={{ color: "var(--text-muted)" }}>
                  Waiting for JSON input...
                </span>
              )}
            </div>
          </section>

          {/* MIDDLE: YouTube + controls */}
          <section style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-base)" }}>
            {/* YouTube link */}
            <div
              className="glass-card"
              style={{
                background: "var(--surface-card)",
                border: "1px solid var(--surface-border)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--spacing-lg)",
              }}
            >
              <div style={{ 
                color: "var(--text-secondary)", 
                fontWeight: "var(--font-weight-bold)", 
                marginBottom: "var(--spacing-sm)",
                fontSize: "var(--font-size-sm)",
              }}>
                🎥 YouTube Video
              </div>
              <input
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                style={{
                  width: "100%",
                  borderRadius: "var(--radius-md)",
                  background: "rgba(0, 0, 0, 0.3)",
                  border: "1px solid var(--surface-border)",
                  padding: "var(--spacing-sm)",
                  color: "var(--text-primary)",
                  fontFamily: "var(--font-mono)",
                  fontSize: "var(--font-size-sm)",
                }}
              />
              <div style={{ 
                marginTop: "var(--spacing-sm)", 
                fontSize: "var(--font-size-xs)", 
                color: "var(--text-muted)",
                fontFamily: "var(--font-mono)",
              }}>
                <div>ID: {videoId || "—"}</div>
                <div>Sync: {syncMs}ms · Mode: <span style={{
                  color: previewMode === "export" ? "var(--accent-warning)" : "var(--accent-success)",
                  fontWeight: "var(--font-weight-bold)",
                }}>{previewMode}</span></div>
              </div>
            </div>

            {/* YouTube player + controls */}
            <div
              className="glass-card"
              style={{
                background: "var(--surface-card)",
                border: "1px solid var(--surface-border)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--spacing-lg)",
              }}
            >
              <div style={{ 
                color: "var(--text-secondary)", 
                fontWeight: "var(--font-weight-bold)", 
                marginBottom: "var(--spacing-sm)",
                fontSize: "var(--font-size-sm)",
              }}>
                ▶️ Player
              </div>

              <div
                style={{
                  borderRadius: "var(--radius-md)",
                  overflow: "hidden",
                  border: "1px solid var(--surface-border)",
                  background: "#000",
                }}
              >
                <div ref={ytHostRef} />
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "var(--spacing-sm)",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginTop: "var(--spacing-base)",
                }}
              >
                <button
                  onClick={togglePlay}
                  disabled={!canPlay}
                  className="btn"
                  style={{
                    borderRadius: "var(--radius-md)",
                    background: isPlaying
                      ? "var(--accent-danger)"
                      : "var(--accent-success)",
                    color: "white",
                    fontWeight: "var(--font-weight-bold)",
                    padding: "var(--spacing-sm) var(--spacing-base)",
                    border: "none",
                    cursor: canPlay ? "pointer" : "not-allowed",
                    opacity: canPlay ? 1 : 0.5,
                    fontSize: "var(--font-size-sm)",
                  }}
                >
                  {isPlaying
                    ? <><span role="img" aria-label="pause">⏸️</span> Pause</>
                    : needsRestart
                    ? <><span role="img" aria-label="play">▶️</span> Play</>
                    : <><span role="img" aria-label="play">▶️</span> Play</>}
                </button>

                <button
                  onClick={() => buildExport()}
                  disabled={!scoreLive}
                  className="btn btn-primary"
                  style={{
                    borderRadius: "var(--radius-md)",
                    background: "var(--accent-primary)",
                    color: "white",
                    fontWeight: "var(--font-weight-bold)",
                    padding: "var(--spacing-sm) var(--spacing-base)",
                    border: "none",
                    cursor: scoreLive ? "pointer" : "not-allowed",
                    opacity: scoreLive ? 1 : 0.5,
                    fontSize: "var(--font-size-sm)",
                  }}
                >
                  <span role="img" aria-label="refresh">🔄</span> Export
                </button>

                <label style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-xs)", flex: "1 1 auto", minWidth: 120 }}>
                  <div style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)", fontWeight: "var(--font-weight-bold)" }}>
                    Speed
                  </div>
                  <input
                    type="range"
                    min={0.25}
                    max={2.0}
                    step={0.05}
                    value={speed}
                    onChange={(e) => {
                      const newSpeed = Number(e.target.value);
                      setSpeed(newSpeed);
                    }}
                    disabled={!ytReady}
                    style={{ width: "100%" }}
                  />
                  <div
                    style={{
                      fontSize: "var(--font-size-xs)",
                      color: "var(--text-muted)",
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    {speed.toFixed(2)}×
                  </div>
                </label>

                <div
                  style={{
                    marginLeft: "auto",
                    color: "var(--text-muted)",
                    fontFamily: "var(--font-mono)",
                    fontSize: "var(--font-size-xs)",
                    display: "flex",
                    gap: "var(--spacing-sm)",
                    flexWrap: "wrap",
                  }}
                >
                  <span>YT: {Math.round(ytMsNow)}ms</span>
                  <span>·</span>
                  <span>Pos: {Math.round(posMs)}ms</span>
                  <span>·</span>
                  <span>Sync: {syncMs}ms</span>
                  <span>·</span>
                  <span style={{ color: Math.abs(ytMsNow - posMs - syncMs) < 50 ? "var(--accent-success)" : "var(--accent-danger)" }}>
                    Drift: {ytMsNow > 0 ? Math.round(ytMsNow - posMs - syncMs) : 0}ms
                  </span>
                </div>
              </div>

              {/* Tuning controls */}
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  flexWrap: "wrap",
                  marginTop: 12,
                }}
              >
                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 800 }}>
                    Lookahead (ms)
                  </div>
                  <input
                    type="number"
                    value={lookaheadMs}
                    onChange={(e) =>
                      setLookaheadMs(
                        Math.max(500, Math.min(6000, Number(e.target.value) || 2200))
                      )
                    }
                    style={{
                      width: 140,
                      borderRadius: 10,
                      background: "#0f172a",
                      border: "1px solid #334155",
                      padding: "0.5rem",
                      color: "#f1f5f9",
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      fontSize: 12,
                    }}
                  />
                </label>

                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 800 }}>
                    Hit (ms)
                  </div>
                  <input
                    type="number"
                    value={hitMs}
                    onChange={(e) =>
                      setHitMs(
                        Math.max(10, Math.min(300, Number(e.target.value) || 80))
                      )
                    }
                    style={{
                      width: 120,
                      borderRadius: 10,
                      background: "#0f172a",
                      border: "1px solid #334155",
                      padding: "0.5rem",
                      color: "#f1f5f9",
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      fontSize: 12,
                    }}
                  />
                </label>

                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 800 }}>
                    Sync Offset (ms)
                  </div>
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    <button
                      onClick={() => {
                        const v = syncMs - 100;
                        setSyncMs(v);
                        localStorage.setItem(`drumly:syncMs:${videoId}`, String(v));
                      }}
                      style={{
                        borderRadius: 6,
                        background: "#374151",
                        border: "none",
                        color: "#f1f5f9",
                        padding: "0.4rem 0.5rem",
                        fontSize: 10,
                        cursor: "pointer",
                      }}
                    >
                      -100
                    </button>
                    <button
                      onClick={() => {
                        const v = syncMs - 10;
                        setSyncMs(v);
                        localStorage.setItem(`drumly:syncMs:${videoId}`, String(v));
                      }}
                      style={{
                        borderRadius: 6,
                        background: "#374151",
                        border: "none",
                        color: "#f1f5f9",
                        padding: "0.4rem 0.5rem",
                        fontSize: 10,
                        cursor: "pointer",
                      }}
                    >
                      -10
                    </button>
                    <input
                      type="number"
                      value={syncMs}
                      onChange={(e) => {
                        const v = Math.round(Number(e.target.value) || 0);
                        setSyncMs(v);
                        localStorage.setItem(`drumly:syncMs:${videoId}`, String(v));
                      }}
                      style={{
                        width: 80,
                        borderRadius: 10,
                        background: "#0f172a",
                        border: "1px solid #334155",
                        padding: "0.5rem",
                        color: "#f1f5f9",
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        fontSize: 12,
                        textAlign: "center",
                      }}
                    />
                    <button
                      onClick={() => {
                        const v = syncMs + 10;
                        setSyncMs(v);
                        localStorage.setItem(`drumly:syncMs:${videoId}`, String(v));
                      }}
                      style={{
                        borderRadius: 6,
                        background: "#374151",
                        border: "none",
                        color: "#f1f5f9",
                        padding: "0.4rem 0.5rem",
                        fontSize: 10,
                        cursor: "pointer",
                      }}
                    >
                      +10
                    </button>
                    <button
                      onClick={() => {
                        const v = syncMs + 100;
                        setSyncMs(v);
                        localStorage.setItem(`drumly:syncMs:${videoId}`, String(v));
                      }}
                      style={{
                        borderRadius: 6,
                        background: "#374151",
                        border: "none",
                        color: "#f1f5f9",
                        padding: "0.4rem 0.5rem",
                        fontSize: 10,
                        cursor: "pointer",
                      }}
                    >
                      +100
                    </button>
                  </div>
                </label>

                <button
                  onClick={() =>
                    setPreviewMode((m) => (m === "export" ? "live" : "export"))
                  }
                  disabled={!exportJson}
                  style={{
                    marginLeft: "auto",
                    borderRadius: 12,
                    background:
                      previewMode === "export"
                        ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                        : "#0f172a",
                    border: "1px solid #334155",
                    color: previewMode === "export" ? "#fff" : "#cbd5e1",
                    fontWeight: 900,
                    padding: "0.7rem 1.1rem",
                    cursor: exportJson ? "pointer" : "not-allowed",
                    opacity: exportJson ? 1 : 0.45,
                  }}
                >
                  {previewMode === "export"
                    ? "🧪 Note Rain: Export"
                    : "👀 Note Rain: Live"}
                </button>
              </div>
            </div>
          </section>

          {/* RIGHT: Export JSON */}
          <section
            className="glass-card"
            style={{
              background: "var(--surface-card)",
              border: "1px solid var(--surface-border)",
              borderRadius: "var(--radius-lg)",
              padding: "var(--spacing-lg)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--spacing-sm)",
                flexWrap: "wrap",
                marginBottom: "var(--spacing-sm)",
              }}
            >
              <div style={{ 
                color: "var(--text-secondary)", 
                fontWeight: "var(--font-weight-bold)",
                fontSize: "var(--font-size-sm)",
              }}>
                💾 Export Data
              </div>
              <div style={{ marginLeft: "auto", display: "flex", gap: "var(--spacing-xs)", flexWrap: "wrap" }}>
                <button
                  onClick={copyExport}
                  disabled={!exportJson}
                  className="btn btn-secondary"
                  style={{
                    borderRadius: "var(--radius-md)",
                    background: "transparent",
                    border: "1px solid var(--surface-border)",
                    color: "var(--text-secondary)",
                    padding: "var(--spacing-xs) var(--spacing-sm)",
                    fontWeight: "var(--font-weight-bold)",
                    cursor: exportJson ? "pointer" : "not-allowed",
                    opacity: exportJson ? 1 : 0.5,
                    fontSize: "var(--font-size-xs)",
                  }}
                >
                  📋 Copy
                </button>
                <button
                  onClick={downloadExport}
                  disabled={!exportJson}
                  className="btn btn-secondary"
                  style={{
                    borderRadius: "var(--radius-md)",
                    background: "transparent",
                    border: "1px solid var(--surface-border)",
                    color: "var(--text-secondary)",
                    padding: "var(--spacing-xs) var(--spacing-sm)",
                    fontWeight: "var(--font-weight-bold)",
                    cursor: exportJson ? "pointer" : "not-allowed",
                    opacity: exportJson ? 1 : 0.5,
                    fontSize: "var(--font-size-xs)",
                  }}
                >
                  ⬇️ Download
                </button>
                <button
                  onClick={saveToDatabase}
                  disabled={!exportJson}
                  className="btn btn-success"
                  style={{
                    borderRadius: "var(--radius-md)",
                    background: exportJson ? "var(--accent-success)" : "transparent",
                    border: "1px solid var(--surface-border)",
                    color: exportJson ? "#fff" : "var(--text-secondary)",
                    padding: "var(--spacing-xs) var(--spacing-sm)",
                    fontWeight: "var(--font-weight-bold)",
                    cursor: exportJson ? "pointer" : "not-allowed",
                    opacity: exportJson ? 1 : 0.5,
                    fontSize: "var(--font-size-xs)",
                  }}
                >
                  💾 Save
                </button>
              </div>
            </div>

            <pre
              style={{
                overflow: "auto",
                maxHeight: 280,
                minHeight: 200,
                fontSize: "var(--font-size-xs)",
                background: "rgba(0, 0, 0, 0.3)",
                border: "1px solid var(--surface-border)",
                borderRadius: "var(--radius-md)",
                padding: "var(--spacing-sm)",
                color: "var(--text-primary)",
                fontFamily: "var(--font-mono)",
                margin: 0,
              }}
            >
              {exportJson
                ? JSON.stringify(exportJson, null, 2)
                : "// Click Play to generate export data"}
            </pre>
          </section>
        </div>

        {/* Full Width: Note Rain */}
        <div style={{ marginTop: "var(--spacing-xl)" }}>
          {activeScore ? (
            <div
              className="glass-card"
              style={{
                background: "var(--surface-card)",
                border: "1px solid var(--surface-border)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--spacing-lg)",
              }}
            >
              <div style={{ 
                color: "var(--text-secondary)", 
                fontWeight: "var(--font-weight-bold)", 
                marginBottom: "var(--spacing-sm)",
                fontSize: "var(--font-size-sm)",
              }}>
                🎵 Note Rain{" "}
                <span style={{ color: "var(--text-muted)", fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-normal)" }}>
                  ({previewMode === "export" ? "export" : "live"})
                </span>
              </div>
              <FallingNotesStage
                score={activeScore}
                tempoIndex={activeTempoIndex}
                posMs={posMs}
                activeMask={activeMask}
                lookaheadMs={lookaheadMs}
                hitWindowMs={Math.max(10, Math.min(180, hitMs * 0.35))}
                speed={speed}
              />
            </div>
          ) : (
            <div
              className="glass-card"
              style={{
                background: "var(--surface-card)",
                border: "1px solid var(--surface-border)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--spacing-xl)",
                color: "var(--text-muted)",
                textAlign: "center",
              }}
            >
              Parse JSON and prepare YouTube video to preview notes
            </div>
          )}
        </div>

        {/* Full Width: Timeline Editor */}
        {exportJson && exportJson.dt && exportJson.m && (
          <div
            className="glass-card"
            style={{
              marginTop: "var(--spacing-xl)",
              background: "var(--surface-card)",
              border: hasUnsavedChanges ? "2px solid var(--accent-warning)" : "1px solid var(--surface-border)",
              borderRadius: "var(--radius-lg)",
              padding: "var(--spacing-lg)",
            }}
          >
            {hasUnsavedChanges && (
              <div
                style={{
                  background: "rgba(245, 158, 11, 0.1)",
                  border: "1px solid var(--accent-warning)",
                  borderRadius: "var(--radius-md)",
                  padding: "var(--spacing-sm) var(--spacing-base)",
                  marginBottom: "var(--spacing-base)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "var(--spacing-sm)",
                }}
              >
                <span style={{ color: "var(--accent-warning)", fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-medium)" }}>
                  ⚠️ Unsaved changes
                </span>
                <div style={{ display: "flex", gap: "var(--spacing-xs)" }}>
                  <button
                    onClick={() => setEditedEvents(null)}
                    className="btn btn-secondary"
                    style={{
                      borderRadius: "var(--radius-md)",
                      background: "transparent",
                      border: "1px solid var(--surface-border)",
                      color: "var(--text-secondary)",
                      padding: "var(--spacing-xs) var(--spacing-sm)",
                      fontSize: "var(--font-size-xs)",
                      cursor: "pointer",
                    }}
                  >
                    ❌ Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (editedEvents && exportJson) {
                        const newDt = editedEvents.map(e => e.ms);
                        const newM = editedEvents.map(e => e.m);
                        setExportJson({
                          ...exportJson,
                          dt: newDt,
                          m: newM,
                        });
                        setEditedEvents(null);
                      }
                    }}
                    className="btn btn-success"
                    style={{
                      borderRadius: "var(--radius-md)",
                      background: "var(--accent-success)",
                      border: "none",
                      color: "white",
                      padding: "var(--spacing-xs) var(--spacing-sm)",
                      fontSize: "var(--font-size-xs)",
                      fontWeight: "var(--font-weight-bold)",
                      cursor: "pointer",
                    }}
                  >
                    ✅ Apply Changes
                  </button>
                </div>
              </div>
            )}
            <TimelineEditor
              events={editedEvents || exportJson.dt.map((dt: number, i: number) => ({
                ms: dt,
                m: exportJson.m[i] || 0,
              }))}
              syncMs={syncMs}
              onEventsChange={(newEvents) => {
                setEditedEvents(newEvents);
              }}
              onSyncMsChange={handleSyncMsChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}
